import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, currency, daysUntil } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, FileText, Sparkles, Timer, Trash2, ArrowLeft } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

const STATUS_LABELS = {
  draft: "Draft",
  in_progress: "In progress",
  submitted: "Submitted",
  awarded: "Awarded",
  rejected: "Rejected",
};
const STATUS_COLORS = {
  draft: "bg-neutral-100 text-neutral-700",
  in_progress: "bg-amber-100 text-amber-700",
  submitted: "bg-blue-100 text-blue-700",
  awarded: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};

export function ApplicationsList() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/api/applications/").then((r) => setApps(r.data)).finally(() => setLoading(false));
  }, []);

  const sortedApps = useMemo(
    () => [...apps].sort((a, b) => new Date(a.grant_deadline) - new Date(b.grant_deadline)),
    [apps]
  );

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <div className="mb-8">
          <div className="overline mb-2">PIPELINE</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight" data-testid="apps-title">Your applications.</h1>
          <p className="text-neutral-600 mt-2">Every grant you're chasing, ranked by deadline.</p>
        </div>
        {loading ? (
          <div className="text-neutral-500">Loading…</div>
        ) : apps.length === 0 ? (
          <div className="border border-dashed border-neutral-300 rounded-2xl p-16 text-center">
            <div className="font-display text-2xl font-semibold mb-2">No applications yet.</div>
            <p className="text-neutral-600 mb-6">Start with a grant that matches your organization.</p>
            <Link to="/grants" className="btn-primary" data-testid="empty-to-grants">Browse grants</Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {sortedApps.map((a) => {
              const d = daysUntil(a.grant_deadline);
              return (
                <Link to={`/applications/${a.id}`} key={a.id} className="gf-card p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`app-row-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <Badge className={STATUS_COLORS[a.status]} variant="secondary">{STATUS_LABELS[a.status]}</Badge>
                      <span className="text-xs text-neutral-500">{a.documents?.length || 0} docs</span>
                    </div>
                    <div className="font-medium truncate">{a.grant_title}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-medium ${d < 14 ? "text-red-600" : "text-neutral-800"}`}>
                      <Timer size={14} className="inline mr-1" /> {d}d — {new Date(a.grant_deadline).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">Updated {new Date(a.updated_at).toLocaleDateString()}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function ApplicationDetail() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [grant, setGrant] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const a = await api.get(`/api/applications/${id}`);
    setApp(a.data);
    const g = await api.get(`/api/grants/${a.data.grant_id}`);
    setGrant(g.data);
    const all = await api.get(`/api/documents/`);
    setDocs(all.data.filter((d) => (a.data.documents || []).includes(d.id)));
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  const updateStatus = async (s) => {
    const { data } = await api.patch(`/api/applications/${id}`, { status: s });
    setApp(data);
    toast.success(`Status → ${STATUS_LABELS[s]}`);
  };

  const remove = async () => {
    if (!confirm("Delete this application?")) return;
    await api.delete(`/api/applications/${id}`);
    toast.success("Deleted");
    window.location.href = "/applications";
  };

  if (loading || !app || !grant) return <div className="p-16 text-center text-neutral-500">Loading…</div>;
  const d = daysUntil(app.grant_deadline);
  const requirements = grant.requirements || [];
  const generatedTypes = new Set(docs.map((doc) => doc.doc_type));

  const typeMap = {
    "Business Plan": "business_plan",
    "Cover Letter": "cover_letter",
    "Project Narrative": "project_narrative",
    "Budget Justification": "budget_justification",
    "Executive Summary": "executive_summary",
    "Mission Statement": "mission_statement",
    "Organizational Chart": "organizational_chart",
    "Sustainability Plan": "sustainability_plan",
  };

  const generateFor = async (label) => {
    const doc_type = typeMap[label];
    if (!doc_type) return toast.error("Unsupported requirement");
    try {
      const { data } = await api.post("/api/documents/generate", {
        doc_type, grant_id: grant.id, application_id: app.id,
      });
      toast.success(`${label} drafted`);
      window.location.href = `/documents/${data.id}`;
    } catch (e) {
      if (e.response?.status === 402) window.location.href = "/pricing";
      else toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <Link to="/applications" className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 mb-6"><ArrowLeft size={16} /> All applications</Link>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Badge className={STATUS_COLORS[app.status]} variant="secondary">{STATUS_LABELS[app.status]}</Badge>
            <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tight" data-testid="app-title">{app.grant_title}</h1>
            <div className="mt-2 text-neutral-600">{grant.agency} · {currency(grant.amount_min)} – {currency(grant.amount_max)}</div>

            <div className="mt-8">
              <div className="overline mb-3">DOCUMENT CHECKLIST</div>
              <ul className="space-y-3">
                {requirements.map((r) => {
                  const done = generatedTypes.has(typeMap[r]);
                  return (
                    <li key={r} className="border border-neutral-200 rounded-lg p-4 flex items-center justify-between" data-testid={`req-${r}`}>
                      <div className="flex items-center gap-3">
                        {done ? <CheckCircle2 size={20} className="text-emerald-600" /> : <Circle size={20} className="text-neutral-300" />}
                        <span className={`font-medium ${done ? "text-neutral-500 line-through" : ""}`}>{r}</span>
                      </div>
                      {done ? (
                        <Link to={`/documents/${docs.find((doc) => doc.doc_type === typeMap[r])?.id}`} className="btn-ghost text-xs">Open</Link>
                      ) : (
                        <button onClick={() => generateFor(r)} className="btn-accent text-xs"><Sparkles size={12} /> Draft</button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="mt-10">
              <div className="overline mb-3">GENERATED DOCUMENTS</div>
              {docs.length === 0 ? (
                <div className="text-sm text-neutral-500">No documents yet.</div>
              ) : (
                <ul className="grid md:grid-cols-2 gap-3">
                  {docs.map((doc) => (
                    <li key={doc.id} className="border border-neutral-200 rounded-lg p-4 flex items-center gap-3">
                      <FileText size={18} className="text-neutral-500" />
                      <Link to={`/documents/${doc.id}`} className="text-sm font-medium hover:underline flex-1">{doc.title}</Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="gf-card p-6 lg:sticky lg:top-24">
              <div className={`text-sm font-medium ${d < 14 ? "text-red-600" : "text-neutral-800"}`}>
                <Timer size={14} className="inline mr-1" /> {d}d — {new Date(app.grant_deadline).toLocaleDateString()}
              </div>
              <div className="mt-6 space-y-2">
                <label className="text-xs uppercase tracking-widest text-neutral-500">Status</label>
                <Select value={app.status} onValueChange={updateStatus}>
                  <SelectTrigger data-testid="app-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Link to={`/generate?grant=${grant.id}`} className="mt-5 btn-primary w-full justify-center" data-testid="app-generate">
                <Sparkles size={16} /> Draft with AI
              </Link>
              <button onClick={remove} className="mt-3 text-xs text-red-600 hover:underline w-full text-center" data-testid="app-delete">
                <Trash2 size={12} className="inline mr-1" /> Delete application
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
